/**
 * Картка договору — ОКРЕМА СТОРIНКА (рішення власника 2026-07-23):
 * відкривається з реєстру договорів та з картки контрагента (не Drawer).
 *
 * Роути: /refs/contracts/new?contractor_id=X (створення, контрагент
 * предзаповнений) та /refs/contracts/:id (редагування).
 *
 * ч.937 (рішення власника): НОВИЙ ДИЗАЙН (Закон #10) — DocLayout/DocBlock/
 * DocRail; вкладку «Додатково» прибрано: її вміст переїхав на першу вкладку
 * («Основна стаття витрат» — на «Рахунки обліку»). Обовʼязкові поля мають « *».
 * Права колонка: прогрес заповнення + УСІ документи договору (не ланцюг).
 *
 * 3 вкладки: Загальні / Рахунки обліку / Податковий облік (поля 0372-0373).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import {
  contractorNested, orgs, contractors as contractorsApi,
  type ContractRow, type VatMethod,
} from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { projects as projectsApi } from '@/api/projects';
import {
  contractAccountRules, PURPOSE_LABELS, SOURCE_LABELS,
} from '@/api/contractAccountRules';
import AccountSelect from '@/components/AccountSelect';
import SearchSelect from '@/components/SearchSelect';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocLayout from '@/shared/components/DocLayout';
import DocRail from '@/shared/components/DocRail';
import ContractDocsCard from '@/pages/refs/ContractDocsCard';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useConfirm } from '@/components/ConfirmProvider';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { CONTRACT_TYPE_LABELS } from '@/shared/utils/contractTypes';
import { useDefaults } from '@/store/defaults';
import { invalidateRefLists } from '@/shared/utils/refLists';

const VAT_METHOD_LABELS: Record<VatMethod, string> = {
  FIRST_EVENT: 'За першою подією',
  ON_PAYMENT: 'За касовим методом (за оплатою)',
};

type Tab = 'main' | 'accounts' | 'tax';

interface ContractDetail extends ContractRow {
  contractor_name?: string | null;
  organization_name?: string | null;
}

// Редагована панель рахунків (та сама механіка, що на вкладці контрагента).
function AccountsTab({ contractId, organizationId, costItem }: {
  contractId: string; organizationId: string;
  /** ч.937: «Основна стаття витрат» переїхала сюди з вкладки «Додатково». */
  costItem: React.ReactNode;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['contract-accounts-eff', contractId],
    queryFn: () => contractAccountRules.effective({
      organization_id: organizationId, contract_id: contractId,
    }),
  });
  const cardRule = useQuery({
    queryKey: ['contract-card-rule', contractId],
    queryFn: () => contractAccountRules.list({
      organization_id: organizationId, contract_id: contractId,
    }).then((rules) => rules.find(
      (r) => r.contract_id === contractId && !r.contractor_id && !r.contract_type,
    ) ?? null),
  });
  const save = useMutation({
    mutationFn: (patch: Record<string, string | null>) => {
      const cur = cardRule.data;
      return contractAccountRules.putCard({
        organization_id: organizationId,
        contract_id: contractId,
        receivable_account_id: cur?.receivable_account_id ?? null,
        payable_account_id: cur?.payable_account_id ?? null,
        advance_received_account_id: cur?.advance_received_account_id ?? null,
        advance_issued_account_id: cur?.advance_issued_account_id ?? null,
        vat_obligation_account_id: cur?.vat_obligation_account_id ?? null,
        vat_unconfirmed_account_id: cur?.vat_unconfirmed_account_id ?? null,
        ...patch,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-accounts-eff', contractId] });
      qc.invalidateQueries({ queryKey: ['contract-card-rule', contractId] });
    },
  });
  const editable: Record<string, string> = {
    receivable: 'receivable_account_id',
    payable: 'payable_account_id',
    advance_received: 'advance_received_account_id',
    advance_issued: 'advance_issued_account_id',
  };
  if (q.isLoading) return <p style={{ color: 'var(--muted)' }}>Завантаження…</p>;
  if (q.isError || !q.data) return <p style={{ color: 'var(--muted)' }}>Не вдалося завантажити рахунки.</p>;
  return (
    <div style={{ maxWidth: 760 }}>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
        Зміна рахунку створює персональне правило договору (сторінка
        «Рахунки обліку договорів» покаже його серед нестандартних).
        Порожньо — діє стандарт/правило виду/нерезидент автоматично.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
        <tbody>
          {q.data.map((ln) => (
            <tr key={ln.purpose} style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.3rem 0.4rem', color: 'var(--muted)', width: 240 }}>
                {PURPOSE_LABELS[ln.purpose] ?? ln.purpose}
              </td>
              <td style={{ padding: '0.3rem 0.4rem', width: 240 }}>
                {editable[ln.purpose] ? (
                  <AccountSelect
                    value={(ln.source === 'rule:contract' ? ln.account_id : null) ?? ''}
                    onChange={(v) => save.mutate({ [editable[ln.purpose]!]: v || null })}
                    placeholder={`${ln.code ?? ''} (авто)`}
                  />
                ) : (
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{ln.code}</span>
                )}
              </td>
              <td style={{ padding: '0.3rem 0.4rem', color: 'var(--muted)' }}>
                {SOURCE_LABELS[ln.source] ?? ln.source}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '0.9rem', paddingTop: '0.6rem',
        borderTop: '1px dashed var(--border)', maxWidth: 360 }}>
        {costItem}
      </div>
    </div>
  );
}

export default function ContractEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';
  const confirm = useConfirm();

  const [tab, setTab] = useState<Tab>('main');
  // --- form state ---
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [contractorId, setContractorId] = useState(search.get('contractor_id') ?? '');
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [contractType, setContractType] = useState('OTHER');
  const [civilKindId, setCivilKindId] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isZed, setIsZed] = useState(false);
  const [isMain, setIsMain] = useState(false);
  const [vatMethod, setVatMethod] = useState<VatMethod>('FIRST_EVENT');
  const [tareVatMethod, setTareVatMethod] = useState<VatMethod>('FIRST_EVENT');
  const [projectId, setProjectId] = useState('');
  const [paymentTermsType, setPaymentTermsType] = useState('');
  const [debtControlAmount, setDebtControlAmount] = useState(false);
  const [debtLimit, setDebtLimit] = useState('');
  const [prepaymentPercent, setPrepaymentPercent] = useState('');
  const [debtControlDays, setDebtControlDays] = useState(false);
  const [paymentTermDays, setPaymentTermDays] = useState('');
  const [priceTypeId, setPriceTypeId] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [mainCostItemId, setMainCostItemId] = useState('');
  const [vatRateId, setVatRateId] = useState('');
  const [printDealInfo, setPrintDealInfo] = useState(true);
  const [printName, setPrintName] = useState('');
  const [settlementForm, setSettlementForm] = useState('CASHLESS');
  const [comment, setComment] = useState('');  // ч.883
  // П5: ведення взаєморозрахунків (як в 1С).
  const [settlementType, setSettlementType] = useState('BY_CONTRACT');

  // --- довідники ---
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: () => refsApi.currencies(), staleTime: 3600_000 });
  const civilKindsQ = useQuery({ queryKey: ['contract-civil-kinds'], queryFn: () => refsApi.contractCivilKinds.list(), staleTime: 3600_000 });
  const projectsQ = useQuery({ queryKey: ['projects'], queryFn: () => projectsApi.list(), staleTime: 3600_000 });
  const priceTypesQ = useQuery({ queryKey: ['price-types'], queryFn: () => refsApi.priceTypes.list(), staleTime: 3600_000 });
  const costItemsQ = useQuery({ queryKey: ['cost-items'], queryFn: () => refsApi.costItems(), staleTime: 3600_000 });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: () => refsApi.vatRates(), staleTime: 3600_000 });
  const contractorQ = useQuery({
    queryKey: ['contractor-name', contractorId],
    queryFn: () => api.get(`/contractors/${contractorId}`).then((r) => r.data),
    enabled: !!contractorId,
  });
  const contractorsListQ = useQuery({
    queryKey: ['contractors-for-select'],
    queryFn: () => contractorsApi.listAll(),
    enabled: isNew && !search.get('contractor_id'),
  });

  // --- завантаження існуючого ---
  const docQ = useQuery({
    queryKey: ['contract-detail', id],
    queryFn: () => api.get<ContractDetail>(`/refs/contracts/${id}`).then((r) => r.data),
    enabled: !isNew,
  });
  useEffect(() => {
    const c = docQ.data;
    if (!c) return;
    setOrgId(c.organization_id); setContractorId(c.contractor_id);
    setName(c.name); setNumber(c.number ?? '');
    setCurrencyId(c.currency_id); setContractType(c.contract_type);
    setCivilKindId(c.civil_kind_id ?? ''); setContractDate(c.contract_date ?? '');
    setValidUntil(c.valid_until ?? ''); setIsZed(c.is_foreign_economic);
    setIsMain(c.is_main ?? false);
    setVatMethod(c.vat_method); setTareVatMethod(c.tare_vat_method);
    setProjectId(c.project_id ?? '');
    setPaymentTermsType((c as { payment_terms_type?: string | null }).payment_terms_type ?? '');
    setDebtControlAmount(c.debt_control_amount ?? false);
    setDebtLimit(c.debt_limit != null ? String(c.debt_limit) : '');
    setPrepaymentPercent(c.prepayment_percent != null ? String(c.prepayment_percent) : '');
    setDebtControlDays(c.debt_control_days ?? false);
    setPaymentTermDays(c.payment_term_days != null ? String(c.payment_term_days) : '');
    setPriceTypeId(c.price_type_id ?? ''); setMainCostItemId(c.main_cost_item_id ?? '');
    setDiscountPercent(c.discount_percent && Number(c.discount_percent) > 0 ? String(c.discount_percent) : '');
    setVatRateId(c.vat_rate_id ?? '');
    setPrintDealInfo(c.print_deal_info ?? true); setPrintName(c.print_name ?? '');
    setSettlementForm(c.settlement_form ?? 'CASHLESS');
    setComment(c.comment ?? '');
    setSettlementType(c.settlement_type ?? 'BY_CONTRACT');
  }, [docQ.data]);

  const uahId = useMemo(
    () => currenciesQ.data?.find((c: any) => c.code === 'UAH')?.id ?? '',
    [currenciesQ.data],
  );

  // Автонумерація поля «Номер» при СТВОРЕННI: щойно обрано юрособу — тягнемо
  // наступний номер (бухгалтер може перебити вручну; якщо вже щось ввів —
  // не чіпаємо). Для існуючого договору — ні.
  const [numberTouched, setNumberTouched] = useState(false);
  const nextNumberQ = useQuery({
    queryKey: ['contract-next-number', orgId],
    queryFn: () => api.get<{ number: string }>(
      `/refs/contracts-next-number?organization_id=${orgId}`,
    ).then((r) => r.data),
    enabled: isNew && !!orgId,
  });
  useEffect(() => {
    if (isNew && !numberTouched && nextNumberQ.data && !number) {
      setNumber(nextNumberQ.data.number);
    }
  }, [nextNumberQ.data, isNew, numberTouched]);

  // ч.1059: чернетка незбережених правок договору (як у документах) — усі
  // поля форми одним обʼєктом. Вкладення/інші вкладені мутації договір НЕ
  // зберігають — тому чернетку стираємо лише явним markSaved.
  const draftValue = useMemo(() => ({
    orgId, contractorId, name, number, currencyId, contractType,
    civilKindId, contractDate, validUntil, isZed, isMain, vatMethod,
    tareVatMethod, projectId, paymentTermsType, debtControlAmount, debtLimit, prepaymentPercent,
    debtControlDays, paymentTermDays, priceTypeId, discountPercent, mainCostItemId, vatRateId,
    printDealInfo, printName, settlementForm, comment, settlementType, numberTouched,
  }), [
    orgId, contractorId, name, number, currencyId, contractType,
    civilKindId, contractDate, validUntil, isZed, isMain, vatMethod,
    tareVatMethod, projectId, paymentTermsType, debtControlAmount, debtLimit, prepaymentPercent,
    debtControlDays, paymentTermDays, priceTypeId, discountPercent, mainCostItemId, vatRateId,
    printDealInfo, printName, settlementForm, comment, settlementType, numberTouched,
  ]);
  const restoreDraft = useCallback((v: Partial<typeof draftValue>) => {
    if (v.orgId !== undefined) setOrgId(v.orgId);
    if (v.contractorId !== undefined) setContractorId(v.contractorId);
    if (v.name !== undefined) setName(v.name);
    if (v.number !== undefined) setNumber(v.number);
    if (v.currencyId !== undefined) setCurrencyId(v.currencyId);
    if (v.contractType !== undefined) setContractType(v.contractType);
    if (v.civilKindId !== undefined) setCivilKindId(v.civilKindId);
    if (v.contractDate !== undefined) setContractDate(v.contractDate);
    if (v.validUntil !== undefined) setValidUntil(v.validUntil);
    if (v.isZed !== undefined) setIsZed(v.isZed);
    if (v.isMain !== undefined) setIsMain(v.isMain);
    if (v.vatMethod !== undefined) setVatMethod(v.vatMethod);
    if (v.tareVatMethod !== undefined) setTareVatMethod(v.tareVatMethod);
    if (v.projectId !== undefined) setProjectId(v.projectId);
    if (v.paymentTermsType !== undefined) setPaymentTermsType(v.paymentTermsType);
    if (v.debtControlAmount !== undefined) setDebtControlAmount(v.debtControlAmount);
    if (v.debtLimit !== undefined) setDebtLimit(v.debtLimit);
    if (v.prepaymentPercent !== undefined) setPrepaymentPercent(v.prepaymentPercent);
    if (v.debtControlDays !== undefined) setDebtControlDays(v.debtControlDays);
    if (v.paymentTermDays !== undefined) setPaymentTermDays(v.paymentTermDays);
    if (v.priceTypeId !== undefined) setPriceTypeId(v.priceTypeId);
    if (v.discountPercent !== undefined) setDiscountPercent(v.discountPercent);
    if (v.mainCostItemId !== undefined) setMainCostItemId(v.mainCostItemId);
    if (v.vatRateId !== undefined) setVatRateId(v.vatRateId);
    if (v.printDealInfo !== undefined) setPrintDealInfo(v.printDealInfo);
    if (v.printName !== undefined) setPrintName(v.printName);
    if (v.settlementForm !== undefined) setSettlementForm(v.settlementForm);
    if (v.comment !== undefined) setComment(v.comment);
    if (v.settlementType !== undefined) setSettlementType(v.settlementType);
    if (v.numberTouched !== undefined) setNumberTouched(v.numberTouched);
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    ready: isNew || !!docQ.data,
    subject: 'цього договору',
    clearOnAnyMutation: false,
  });

  const currentOrg = orgList.data?.items.find((o) => o.id === orgId);
  const showVatMethod = currentOrg?.is_vat_payer ?? true;
  const showTareVatMethod = (currentOrg as any)?.use_returnable_packaging ?? false;
  const expired = validUntil && validUntil < new Date().toISOString().slice(0, 10);

  const payload = () => ({
    name: name.trim(), currency_id: currencyId || uahId,
    contract_type: contractType, civil_kind_id: civilKindId || null,
    number: number.trim() || null, contract_date: contractDate || null,
    valid_until: validUntil || null, is_foreign_economic: isZed,
    is_main: isMain,
    vat_method: vatMethod, tare_vat_method: tareVatMethod,
    project_id: projectId || null,
    payment_terms_type: paymentTermsType || null,
    debt_control_amount: debtControlAmount,
    debt_limit: debtLimit || null,
    prepayment_percent: prepaymentPercent || null,
    debt_control_days: debtControlDays,
    payment_term_days: paymentTermDays ? Number(paymentTermDays) : null,
    price_type_id: priceTypeId || null,
    discount_percent: discountPercent || '0',
    main_cost_item_id: mainCostItemId || null,
    vat_rate_id: vatRateId || null,
    print_deal_info: printDealInfo,
    print_name: printName.trim() || null,
    settlement_form: settlementForm,
    comment: comment.trim() || null,
    settlement_type: settlementType,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return contractorNested.createContract({
          contractor_id: contractorId, organization_id: orgId, ...payload(),
        } as any);
      }
      return contractorNested.updateContract(id!, payload() as any);
    },
    onSuccess: (c: any) => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['contract-detail', id] });
      invalidateRefLists(qc, 'contracts');   // ч.1267: уся родина ключів списків договорів
      if (isNew && c?.id) navigate(`/refs/contracts/${c.id}`, { replace: true });
    },
  });

  const remove = useMutation({
    mutationFn: () => contractorNested.removeContract(id!),
    onSuccess: () => {
      draft.markSaved();
      invalidateRefLists(qc, 'contracts');
      navigate('/contracts-registry');
    },
  });

  const canSave = !!name.trim() && !!contractorId && (isNew ? !!orgId : true)
    && !save.isPending;

  const contractorName = docQ.data?.contractor_name
    ?? (contractorQ.data as any)?.name ?? '';

  // ч.937: прогрес заповнення — ЛИШЕ обовʼязкові поля (ті, що з « *»).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 4,
      filled: (contractorId ? 1 : 0) + (orgId ? 1 : 0) + (name.trim() ? 1 : 0)
        + ((currencyId || uahId) ? 1 : 0) },
    { id: 'terms', title: 'Умови роботи', required: 0, filled: 0 },
    { id: 'control', title: 'Контроль заборгованості', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const num = (bid: string) => blocks.findIndex((b) => b.id === bid) + 1;
  // ч.937: «Основна стаття витрат» — на вкладці «Рахунки обліку» (рішення власника).
  const costItemField = (
    <div className="field" style={{ margin: 0 }}>
      <label>Основна стаття витрат</label>
      <SearchSelect
        items={(costItemsQ.data ?? []).map((t: any) => ({ id: t.id, label: t.name, hint: t.code ?? null }))}
        value={mainCostItemId}
        placeholder="— не вказано —"
        onChange={(id) => setMainCostItemId(id)}
      />
      <div style={{ fontSize: '0.78em', color: 'var(--muted)', marginTop: '0.2rem' }}>
        Довідкове поле договору: підставляється у документи. На проводки не впливає —
        рахунки визначають правила вище.
      </div>
    </div>
  );

  return (
    <div className="card">
      <div className="toolbar" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>
            {isNew ? 'Новий договір' : `Договір: ${name || '…'}`}
            {contractorName && (
              <span style={{ color: 'var(--muted)', fontWeight: 400 }}> — {contractorName}</span>
            )}
          </h2>
          {expired && (
            <span style={{ fontSize: '0.75rem', color: '#6b7280',
                           border: '1px solid #d1d5db', borderRadius: 4, padding: '0 6px' }}>
              строк минув
            </span>
          )}
          {save.isSuccess && <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>збережено</span>}
        </div>
        <DocumentToolbar
          onBackToList={() => navigate('/contracts-registry')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave}
          savePending={save.isPending}
          postable={false}
          deletable={!isNew}
          onDelete={() => confirm(`Видалити договір «${name}»?`).then((ok) => {
            if (ok) remove.mutate();
          })}
          deletePending={remove.isPending}
          isNew={isNew}
        />
      </div>

      <div className="seg-tabs">
        {([['main', 'Загальні'], ['accounts', 'Рахунки обліку'], ['tax', 'Податковий облік']] as const).map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={tab === key ? 'active' : ''}>{label}</button>
        ))}
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} isNew={isNew} related={false}
          doc={{ type: 'ref.contracts', id: isNew ? '' : id! }}
          extraCards={<ContractDocsCard contractId={isNew ? undefined : id} />} />
      }>
      {tab === 'main' && (
        <>
        <DocBlock index={num('main')} title="Основні дані">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
            <label>Контрагент <span style={{ color: 'red' }}>*</span>
              {isNew && !search.get('contractor_id') ? (
                <select value={contractorId} onChange={(e) => setContractorId(e.target.value)}
                  style={{ display: 'block', width: '100%' }}>
                  <option value="">— оберіть —</option>
                  {(contractorsListQ.data?.items ?? []).map((k: any) => (
                    <option key={k.id} value={k.id}>{k.name}</option>
                  ))}
                </select>
              ) : (
                <input value={contractorName} disabled
                  style={{ width: '100%', background: 'var(--bg-soft, #f7f7f9)' }} />
              )}
            </label>
            <label>Організація <span style={{ color: 'red' }}>*</span>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                disabled={!isNew} style={{ display: 'block', width: '100%' }}>
                <option value="">— оберіть —</option>
                {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label>Назва <span style={{ color: 'red' }}>*</span>
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Номер
              <input value={number}
                onChange={(e) => { setNumber(e.target.value); setNumberTouched(true); }}
                style={{ width: '100%' }} />
            </label>
            <label>Тип договору
              <select value={contractType} onChange={(e) => setContractType(e.target.value)} style={{ display: 'block', width: '100%' }}>
                {Object.entries(CONTRACT_TYPE_LABELS).map(([t, l]) => <option key={t} value={t}>{l}</option>)}
              </select>
            </label>
            <label>Вид за ЦК
              <select value={civilKindId} onChange={(e) => setCivilKindId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— не вказано —</option>
                {(civilKindsQ.data ?? []).map((k: any) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </label>
            <label>Валюта <span style={{ color: 'red' }}>*</span>
              <select value={currencyId || uahId} onChange={(e) => setCurrencyId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                {(currenciesQ.data ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label>Дата
              <DatePopover value={contractDate} onChange={setContractDate} style={{ width: '100%' }} />
            </label>
            <label>Строк дії
              <DatePopover value={validUntil} onChange={setValidUntil} style={{ width: '100%' }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.6rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={isZed} onChange={(e) => setIsZed(e.target.checked)} style={{ width: 'auto' }} />
              ЗЕД-договір
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              title="Основний договір підставляється у документи за замовчуванням">
              <input type="checkbox" checked={isMain} onChange={(e) => setIsMain(e.target.checked)} style={{ width: 'auto' }} />
              Основний договір контрагента
            </label>
          </div>
        </DocBlock>

        {/* ч.937: вміст колишньої вкладки «Додатково» + умови оплати — тут. */}
        <DocBlock index={num('terms')} title="Умови роботи">
          {/* ч.938 (зауваження власника): «Проект» — в один рядок, одразу за
              «Ведення взаєморозрахунків» (тому 4 колонки, а не 3). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.6rem' }}>
            <label>Тип цін (за замовч.)
              <select value={priceTypeId} onChange={(e) => setPriceTypeId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— не вказано —</option>
                {(priceTypesQ.data ?? []).map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label>Знижка за договором, %
              <input type="number" min="0" max="100" step="0.01" value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                placeholder="0 — без знижки" style={{ width: '100%' }} />
            </label>
            <label title="Розріз обліку боргу: по договору в цілому або по кожній накладній (FIFO-закриття оплат, як в 1С). Дані по документах ведуться завжди — налаштування впливає на контроль/відображення.">
              Ведення взаєморозрахунків
              <select value={settlementType} onChange={(e) => setSettlementType(e.target.value)}
                style={{ display: 'block', width: '100%' }}>
                <option value="BY_CONTRACT">по договору в цілому</option>
                <option value="BY_DOCUMENT">по розрахункових документах</option>
                <option value="BY_BILL">по рахунках</option>
              </select>
            </label>
            <label>Проект (за замовч.)
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— не вказано —</option>
                {(projectsQ.data?.items ?? []).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          </div>
          {/* 2026-07-25 (рішення власника): умови оплати договору —
              підставляються у рахунок при виборі договору. */}
          <div style={{ marginTop: '0.8rem', paddingTop: '0.5rem', borderTop: '1px dashed var(--border)' }}>
            <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginBottom: '0.3rem' }}>
              Умови оплати (підставляються у рахунок)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
              <label>Умови оплати
                <select value={paymentTermsType} onChange={(e) => setPaymentTermsType(e.target.value)}
                  style={{ display: 'block', width: '100%' }}>
                  <option value="">— не вказано —</option>
                  <option value="PREPAY">Повна передплата</option>
                  <option value="PARTIAL">Часткова передплата</option>
                  <option value="POSTPAY">Постоплата</option>
                  <option value="ON_DELIVERY">При отриманні</option>
                </select>
              </label>
              {paymentTermsType === 'PARTIAL' && (
                <label>Передоплата, %
                  <input type="number" value={prepaymentPercent} onChange={(e) => setPrepaymentPercent(e.target.value)}
                    min={0} max={100} style={{ width: '100%' }} />
                </label>
              )}
              {(paymentTermsType === 'PARTIAL' || paymentTermsType === 'POSTPAY') && (
                <label>Відстрочка оплати, днів
                  <input type="number" value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)}
                    min={0} style={{ width: '100%' }} />
                </label>
              )}
            </div>
          </div>
        </DocBlock>

        <DocBlock index={num('control')} title="Контроль заборгованості">
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={debtControlAmount}
                  onChange={(e) => setDebtControlAmount(e.target.checked)} style={{ width: 'auto' }} />
                Контролювати суму заборгованості
              </label>
              <label>Ліміт боргу, грн
                <input type="number" value={debtLimit} onChange={(e) => setDebtLimit(e.target.value)}
                  disabled={!debtControlAmount} style={{ width: '100%' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input type="checkbox" checked={debtControlDays}
                  onChange={(e) => setDebtControlDays(e.target.checked)} style={{ width: 'auto' }} />
                Контролювати число днів заборгованості
              </label>
            </div>
            <div style={{ fontSize: '0.78em', color: 'var(--muted)', marginTop: '0.3rem' }}>
              Контроль днів рахує прострочення від «Відстрочка оплати, днів» умов оплати.
              При перевищенні — попередження при створенні рахунку.
            </div>
          </div>
        </DocBlock>
        </>
      )}

      {tab === 'accounts' && (
        <DocBlock title="Рахунки обліку розрахунків">
          {isNew ? (
            <>
              <p style={{ color: 'var(--muted)' }}>
                Рахунки обліку стануть доступні після збереження договору
                (типово: 361/631, нерезидент — 362/632 автоматично).
              </p>
              <div style={{ maxWidth: 360 }}>{costItemField}</div>
            </>
          ) : (
            <AccountsTab contractId={id!} organizationId={orgId} costItem={costItemField} />
          )}
        </DocBlock>
      )}

      {tab === 'tax' && (
        <DocBlock title="Податковий облік">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
          {showVatMethod && (
            <label>Метод ПДВ (схема податкового обліку)
              <select value={vatMethod} onChange={(e) => setVatMethod(e.target.value as VatMethod)} style={{ display: 'block', width: '100%' }}>
                <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
              </select>
            </label>
          )}
          {showTareVatMethod && (
            <label>Метод ПДВ по тарі
              <select value={tareVatMethod} onChange={(e) => setTareVatMethod(e.target.value as VatMethod)} style={{ display: 'block', width: '100%' }}>
                <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
              </select>
            </label>
          )}
          {showVatMethod && (
            <label>Ставка ПДВ за замовч. (у документи)
              <select value={vatRateId} onChange={(e) => setVatRateId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— з документа —</option>
                {(vatRatesQ.data ?? []).map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
          <label>Форма розрахунків (для ПН)
            <select value={settlementForm} onChange={(e) => setSettlementForm(e.target.value)} style={{ display: 'block', width: '100%' }}>
              <option value="CASHLESS">Безготівкова</option>
              <option value="CASH">Готівкова</option>
              <option value="BARTER">Бартер</option>
              <option value="OTHER">Iнша</option>
            </select>
          </label>
          </div>
        </DocBlock>
      )}
      {/* ч.883: блок «Коментарі та вкладення» за дизайном редакторів.
          Вкладення (2026-08-13): скан договору тощо; на новому — кнопка
          неактивна до збереження. */}
      <DocBlock index={num('extra')} title="Коментарі та вкладення">
        <div className="docblock__cols">
          <div className="field" style={{ margin: 0 }}>
            <label>Коментар</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <div>
            {orgId && (
              <AttachmentsPanel organizationId={orgId}
                documentType={'ref.contracts'} documentId={isNew ? undefined : id} />
            )}
          </div>
        </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
